import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch } from '../../../../redux/hooks';
import { editConnector, fetchAllConnectors } from '../../../../redux/slices/ConnectorSlice';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../../shared-components/button/Button';
import CustomInput from '../../../../shared-components/custom-input/CustomInput';
import ModalBox from '../../../../shared-components/modal-box/ModalBox';
import SelectField from '../../../../shared-components/select-field/SelectField';
import SuccessNotification from '../../../../shared-components/success-notification/SuccessNotification';

const CURRENT_TYPE_OPTIONS = [
  { value: 'AC', label: 'AC' },
  { value: 'DC', label: 'DC' },
];

interface EditConnectorRatingDrawerProperties {
  /** Whether the modal is open */
  open: boolean;
  /** The connector rating data object to edit */
  ratingData: any;
  /** Callback to close the modal */
  onClose: () => void;
  /**
   * Called after a connector rating is successfully updated.
   * @param ratingId - Id of the updated rating
   */
  onRatingUpdated: (ratingId?: string) => void;
}

/**
 * Slide-in drawer modal for editing an existing connector rating.
 * Pre-populates all fields from the selected record and submits a PATCH request.
 * @param {EditConnectorRatingDrawerProperties} properties - Component properties
 * @param {boolean} properties.open - Whether the drawer is visible
 * @param {any} properties.ratingData - Existing rating record to populate
 * @param {Function} properties.onClose - Close callback function
 * @param {Function} properties.onRatingUpdated - Update success callback function
 * @returns {React.JSX.Element} The rendered drawer component
 */
function EditConnectorRatingDrawer({
  open,
  ratingData,
  onClose,
  onRatingUpdated,
}: Readonly<EditConnectorRatingDrawerProperties>): React.JSX.Element {
  const [ratingName, setRatingName] = useState('');
  const [currentType, setCurrentType] = useState('AC');
  const [maxPower, setMaxPower] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showFormErrors, setShowFormErrors] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isRatingNameEdited, setIsRatingNameEdited] = useState(false);
  const { t } = useTranslation('deviceHub');
  const dispatch = useAppDispatch();

  /** Populate fields whenever the drawer opens with new rating data */
  useEffect(() => {
    if (open && ratingData) {
      const initialName = ratingData.name || ratingData.connectorRatingName || '';
      const initialType = (ratingData.currentType || ratingData.type || 'AC').toUpperCase();
      const capacityValue = ratingData.capacityKW ?? ratingData.maxPowerOutput ?? '';
      const initialPower = capacityValue ? capacityValue.toString().replace(/[^0-9.]/g, '') : '';

      setRatingName(initialName);
      setCurrentType(initialType);
      setMaxPower(initialPower);
      setFormError('');
      setShowFormErrors(false);

      const generatedName = initialPower ? `${initialType} ${initialPower} kw` : '';
      setIsRatingNameEdited(initialName !== generatedName);
    }
  }, [open, ratingData]);

  useEffect(() => {
    if(!isRatingNameEdited){
      if(maxPower){
        setRatingName(`${currentType} ${maxPower} kw`);
      }else{
        setRatingName('');
      }
    }
  }, [currentType, maxPower, isRatingNameEdited]);

  /**
   * Resets all form fields to initial state.
   */
  const resetForm = () => {
    setRatingName('');
    setCurrentType('AC');
    setMaxPower('');
    setFormError('');
    setShowFormErrors(false);
    setIsRatingNameEdited(false);
  };

  /**
   * Handles closing the modal and resetting the form.
   */
  const handleClose = () => {
    resetForm();
    onClose();
  };

  /**
   * Validates and submits the connector rating update PATCH request.
   */
  const handleSubmit = async () => {
    setShowFormErrors(true);
    if (!ratingName.trim()) {
      return;
    }
    if (!maxPower.trim()) {
      return;
    }
    const numericPart = maxPower.trim().replace(/[^0-9.]/g, '');
    if (!numericPart) {
      setFormError(t('validMaxPowerError', 'Please enter a valid max power'));
      return;
    }

    setFormError('');
    setSubmitting(true);

    const payload: Record<string, any> = {
      name: ratingName.trim(),
      currentType: currentType.toUpperCase(),
      capacityKW: numericPart,
      unit: ratingData?.unit || 'kW',
    };

    if (ratingData?.isActive !== undefined) {
      payload.isActive = ratingData.isActive;
    }

    const ratingId = ratingData?._id || ratingData?.id;

    try {
      await dispatch(editConnector({ id: ratingId, data: payload })).unwrap();

      dispatch(
        fetchAllConnectors({
          page: 1,
          limit: 10,
        }),
      );

      setShowSuccess(true);
      onRatingUpdated(ratingId);
    } catch (error: any) {
      const message =
        error?.message ||
        error?.response?.data?.message ||
        t('failedToUpdateConnectorRating', 'Failed to update connector rating');
      setFormError(message);
      dispatch(
        setNotificationMessage({
          type: 'error',
          message,
          status: true,
        }),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <ModalBox
        open={open}
        title=""
        content={
          <div className="left-drawer-content rating-drawer">
            <div className="drawer-header">
              <h3 className="drawer-title">{t('editConnectorRating', 'Edit Connector Rating')}</h3>
            </div>
            <div className="drawer-body">
              <div className="drawer-grid-2">
                <SelectField
                  name="ratingCurrentType"
                  inputLabel={t('currentTypeLabel', 'Current Type')}
                  options={CURRENT_TYPE_OPTIONS}
                  placeholder="Choose Current Type"
                  selectedValue={currentType}
                  onChangeValue={(_, value) => {
                    setCurrentType(value.toString());
                    setIsRatingNameEdited(false);
                    if (formError) setFormError('');
                  }}
                  error={showFormErrors && !currentType}
                  errorMessage={t('currentTypeRequired', 'Current type is required')}
                  valueRequired
                />
                <CustomInput
                  type="number"
                  name="ratingMaxPowerInput"
                  inputLabel={t('maxPowerOutput', 'Max Power Output')}
                  placeholder={t('enterPowerPlaceholder', 'Enter max power')}
                  value={maxPower}
                  onChangeValue={(_, value) => {
                    const input = value.toString();
                    if (!/^\d*\.?\d*$/.test(input)) {
                      return;
                    }

                    const number = Number(input);
                    if (number < 10 && !/^\d*\.?\d{0,1}$/.test(input)) {
                      return;
                    }

                    if (number >= 10 && !/^\d*\.?\d{0,3}$/.test(input)) {
                      return;
                    }

                    setMaxPower(input);
                    setIsRatingNameEdited(false);
                    if (formError) {
                      setFormError('');
                    }
                  }}
                  svgIcon={<span className="rating-kw-suffix">kW</span>}
                  error={showFormErrors && !maxPower.trim()}
                  errorMessage={t('maxPowerOutputRequired', 'Max power output is required')}
                  valueRequired
                  maxValue={1000}
                  decimalLimit={3}
                />
              </div>
              <CustomInput
                type="text"
                name="ratingNameInput"
                inputLabel={t('ratingName', 'Rating Name')}
                placeholder={t('enterRatingNamePlaceholder', 'Enter rating name')}
                value={ratingName}
                onChangeValue={(_, value) => {
                  setRatingName(value.toString());
                  setIsRatingNameEdited(true);
                  if (formError) setFormError('');
                }}
                error={showFormErrors && !ratingName.trim()}
                errorMessage={t('ratingNameRequired', 'Rating name is required')}
                valueRequired
                characterLimit={100}
              />
              {formError && <div className="drawer-error">{formError}</div>}
            </div>
            <div className="footer-wrap">
              <div className="button-wrap">
                <MyButton
                  label={t('cancel', 'Cancel')}
                  buttonType="error"
                  onClickFunc={handleClose}
                />
              </div>
              <div className="button-wrap">
                <MyButton
                  label={t('update', 'Update')}
                  buttonType="submit"
                  onClickFunc={handleSubmit}
                  disableButton={submitting}
                  loading={submitting}
                />
              </div>
            </div>
          </div>
        }
        closeModal={handleClose}
        header={false}
        position="right"
        modalBoxAnimation="modalAnimateFromRight"
      />
      <ModalBox
        open={showSuccess}
        title=""
        content={
          <SuccessNotification
            title={t('connectorRatingUpdatedSuccessfully', 'Connector rating updated successfully')}
            description=""
            onNotificationClose={() => {
              setShowSuccess(false);
              resetForm();
              onClose();
            }}
          />
        }
        closeModal={() => {
          setShowSuccess(false);
          resetForm();
          onClose();
        }}
        header={false}
        notification
      />
    </>
  );
}

export default EditConnectorRatingDrawer;
